/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { ConfluenceWorkspaceAri } from '@atlassian/ari/confluence';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import {
	classifyGraphError,
	reportHandledGraphError,
	reportUnexpectedGraphPipelineError,
} from '../utils/graphErrorReporting.ts';
import { hydrateIncomingLinks } from '../utils/hydrateIncomingLinks.ts';
import { readIncomingLinkUrls } from '../utils/readIncomingLinkUrls.ts';
import {
	buildRelatedContentCypher,
	MAX_RELATED_CONTENT,
} from '../utils/relatedContentRelationships.ts';
import { sortLinksByRecency } from '../utils/sortLinksByRecency.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import {
	getRelatedContentFailedEvent,
	getRelatedContentRequestedEvent,
	getRelatedContentSucceededEvent,
} from './relatedContentAnalytics.ts';
import type {
	useRelatedContentQuery$data,
	useRelatedContentQuery$variables,
} from './__generated__/useRelatedContentQuery.graphql.ts';

const RelatedContentQuery = graphql`
	query useRelatedContentQuery(
		$cypherQuery: String!
		$queryContext: String!
		$version: GraphStoreCypherQueryV2VersionEnum
		$first: Int!
	) {
		graphStore_cypherQueryV2(
			query: $cypherQuery
			queryContext: $queryContext
			version: $version
			first: $first
		) @optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
								data {
									# The per-type URL selection, shared with useIncomingLinksQuery; defined in incomingLinkTypes.ts.
									...incomingLinkTypes_node @relay(mask: false)
								}
							}
						}
					}
				}
			}
		}
	}
`;

export type UseRelatedContentResult = {
	enabled: boolean;
	links: EnhancedSmartLink[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

export type UseRelatedContentOptions = {
	/**
	 * Set to `false` to hold the fetch. Lets a caller that is mounted before its UI is
	 * reachable — the Teamwork Graph modal stays mounted while closed — keep the query
	 * off the view page until it is opened. `enabled: false` also comes back out as
	 * `enabled: false`, so a section gated on it stays hidden. Defaults to `true`.
	 */
	enabled?: boolean;
	/** Selects the ARI the content is stored under in the graph. Undefined means page. */
	contentType?: BylineContentType;
};

/**
 * Fetches the content Teamwork Graph identifies as related to the current page, Confluence
 * content and third-party entities alike, hydrates each entity URL into an `EnhancedSmartLink`
 * through ORS, then orders the list newest-first by last-modified (falling back to created).
 *
 * Must be rendered under a SmartCard provider (`useSmartLinkContext`).
 */
export const useRelatedContent = (
	contentId: string | undefined,
	options?: UseRelatedContentOptions,
): UseRelatedContentResult => {
	const { cloudId, activationId } = useSessionData();
	const { connections, store } = useSmartLinkContext();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasRequestedRef = useRef(false);
	const hasSucceededRef = useRef(false);
	const hasReportedErrorRef = useRef(false);
	const skip =
		options?.enabled === false ||
		!contentId ||
		!cloudId ||
		!activationId ||
		!isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment');
	const [links, setLinks] = useState<EnhancedSmartLink[]>([]);
	const [resolving, setResolving] = useState(false);
	const resolvedDataRef = useRef<useRelatedContentQuery$data | undefined>(undefined);
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	const reportError = useCallback(
		(error: ApolloErrorInterop) => {
			reportHandledGraphError(error);
			createEvent(getRelatedContentFailedEvent({ errorType: classifyGraphError(error) })).fire();
		},
		[createEvent],
	);

	// `skip` gates the query, so its first `false` is the moment the fetch fires.
	useEffect(() => {
		if (skip || hasRequestedRef.current) {
			return;
		}
		hasRequestedRef.current = true;
		createEvent(getRelatedContentRequestedEvent()).fire();
	}, [skip, createEvent]);

	const { data, loading, error } = useQueryInterop<
		useRelatedContentQuery$data,
		useRelatedContentQuery$variables
	>(RelatedContentQuery, {
		variables: {
			cypherQuery: skip
				? ''
				: buildRelatedContentCypher(buildContentAri(cloudId, contentId, options?.contentType)),
			queryContext: skip
				? ''
				: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			version: 'V3',
			first: MAX_RELATED_CONTENT,
		},
		skip,
		interopApolloAggClient: true,
		errorPolicy: 'all',
	});

	// Report from the returned `error`, not the query's `onError`. Under `errorPolicy: 'all'`
	// the Apollo route delivers GraphQL errors as `{ data, error }` and never calls `onError`
	// (it only fires for network errors), so a GraphQL failure would otherwise go unreported.
	// Driving off `error` covers both network and GraphQL errors on either route. Fired once;
	// the ref resets when `error` clears so a later refetch can report again.
	useEffect(() => {
		if (!error) {
			hasReportedErrorRef.current = false;
			return;
		}
		if (hasReportedErrorRef.current) {
			return;
		}
		hasReportedErrorRef.current = true;
		reportError(error);
	}, [error, reportError]);

	const urls = useMemo(() => readIncomingLinkUrls(data), [data]);

	useEffect(() => {
		if (skip || loading || !data) {
			resolvedDataRef.current = undefined;
			setLinks([]);
			setResolving(false);
			return;
		}

		let cancelled = false;
		setResolving(true);

		hydrateIncomingLinks(urls, storeRef.current, connections.client)
			.then((resolvedLinks) => {
				if (!cancelled) {
					resolvedDataRef.current = data;
					setLinks(sortLinksByRecency(resolvedLinks));
					setResolving(false);
					if (!hasSucceededRef.current && !error) {
						hasSucceededRef.current = true;
						createEvent(
							getRelatedContentSucceededEvent({
								resolvedCount: resolvedLinks.length,
								totalCount: data.graphStore_cypherQueryV2?.edges?.length ?? 0,
							}),
						).fire();
					}
				}
			})
			.catch((error: unknown) => {
				// Per-URL resolution reports expected ORS failures. This only prevents an
				// unexpected rejection from leaving the hook in a loading state.
				if (!cancelled) {
					reportUnexpectedGraphPipelineError(error);
					resolvedDataRef.current = data;
					setLinks([]);
					setResolving(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [connections.client, createEvent, data, error, loading, skip, urls]);

	const resolutionPending = !skip && data != null && resolvedDataRef.current !== data;

	return { enabled: !skip, links, loading: loading || resolving || resolutionPending, error };
};
